import React, { useState, useEffect } from "react";
import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../loader/Loader";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";

type SubmitModalWithEditAndDeleteProps = {
  title: string;
  children: React.ReactNode;
  updateAsyncOperation?: () => Promise<boolean>;
  closeSubmitModalWithEditAndDelete: CallableFunction;
  childErrorMessage?: string;
  size: false | Breakpoint | undefined;
  isLoading?: boolean;
  submitButtonName?: string;
  closeModalAfterSubmit?: boolean;
  isUpdatePermission: boolean;
  isDeletePermission: boolean;
  confirmMessage: string;
  passwordConfirmMessage?: string;
  setShowSubmitButton: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmitButton: boolean;
  showDeleteButton: boolean;
  showGeneratePasswordButton?: boolean;
  deleteAsyncOperation?: () => Promise<boolean>;
  generatePasswordAsyncOperation?: () => Promise<boolean>;
};

export interface ModalTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export const BootstrapModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export function BootstrapModalTitle(props: ModalTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        minHeight: "36px",
        m: 0,
        p: 1,
        backgroundColor: "primary.main",
        color: (theme) => theme.palette.grey[50],
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 0,
            color: (theme) => theme.palette.grey[50],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const SubmitModalWithEditAndDelete = (
  Props: SubmitModalWithEditAndDeleteProps
) => {
  const {
    title,
    children,
    updateAsyncOperation,
    closeSubmitModalWithEditAndDelete,
    childErrorMessage,
    size,
    isLoading = false,
    closeModalAfterSubmit = true,
    isUpdatePermission,
    isDeletePermission,
    confirmMessage,
    passwordConfirmMessage,
    setShowSubmitButton,
    showSubmitButton,
    showDeleteButton,
    showGeneratePasswordButton,
    deleteAsyncOperation,
    generatePasswordAsyncOperation,
  } = Props;

  const [isOpen, setIsOpen] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [passwordConfirmModalOpen, setPasswordConfirmModalOpen] =
    useState(false);

  const [isOperationInProgress, setOperationInProgress] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      if (childErrorMessage) {
        toast.error(childErrorMessage);
      }
    }
    return () => {
      isFunctionRan = false;
    };
  }, [childErrorMessage]);

  const _closeSubmitModalWithEditAndDelete = () => {
    setIsOpen(false);
    closeSubmitModalWithEditAndDelete();
  };

  const handleClickCloseModal = () => {
    _closeSubmitModalWithEditAndDelete();
  };

  const _executeOperation = async () => {
    if (updateAsyncOperation) {
      if (childErrorMessage) {
        toast.error(childErrorMessage);
      }

      setOperationInProgress(true);
      try {
        const result = await updateAsyncOperation();
        setOperationInProgress(false);
        if (closeModalAfterSubmit && result) {
          _closeSubmitModalWithEditAndDelete();
        }
      } catch (e) {
        setOperationInProgress(false);
        toast.error(e!.toString());
      }
    }
  };

  const handleEditButtonClick = () => {
    setShowSubmitButton(true);
  };

  const handleConfirmModalClose = (value: boolean) => {
    setConfirmModalOpen(false);
  };

  const handleConfirmModalOpen = () => {
    setConfirmModalOpen(true);
  };

  const handlePasswordConfirmModalOpen = () => {
    setPasswordConfirmModalOpen(true);
  };

  const handlePasswordConfirmModalClose = (value: boolean) => {
    setPasswordConfirmModalOpen(false);
  };

  return (
    <BootstrapModal
      open={isOpen}
      onClose={handleClickCloseModal}
      aria-labelledby="customized-dialog-title"
      maxWidth={size}
      fullWidth
      fullScreen={fullScreen}
      sx={{ filter: isLoading ? "blur(0.5px)" : "none" }}
    >
      <BootstrapModalTitle
        id="customized-dialog-title"
        onClose={handleClickCloseModal}
      >
        <Typography>{title}</Typography>
      </BootstrapModalTitle>
      <DialogContent dividers>
        <div>{children}</div>
      </DialogContent>
      <DialogActions>
        {isUpdatePermission && showGeneratePasswordButton && (
          <Button
            variant="contained"
            onClick={handlePasswordConfirmModalOpen}
            sx={{ marginRight: "auto" }}
            size="small"
          >
            Generate Password
          </Button>
        )}
        {isUpdatePermission && !showSubmitButton && (
          <Button
            variant="contained"
            onClick={handleEditButtonClick}
            size="small"
          >
            Edit
          </Button>
        )}
        {/* {isDeletePermission && showDeleteButton && !showSubmitButton && (
          <Button
            variant="contained"
            onClick={handleConfirmModalOpen}
            size="small"
            color="error"
          >
            Delete
          </Button>
        )} */}
        {showSubmitButton && (
          <Button
            variant="contained"
            onClick={() => _executeOperation()}
            size="small"
          >
            Submit
          </Button>
        )}
        <Button variant="outlined" onClick={handleClickCloseModal} size="small">
          Cancel
        </Button>
      </DialogActions>
      <Loader isLoading={isOperationInProgress || isLoading} />
      {/* {isDeletePermission && (
        <ConfirmModal
          onClose={handleConfirmModalClose}
          asyncOperation={deleteAsyncOperation}
          open={confirmModalOpen}
          closeSubmitModal={_closeSubmitModalWithEditAndDelete}
          closeModalAfterSubmit={closeModalAfterSubmit}
          confirmMessage={confirmMessage}
        ></ConfirmModal>
      )} */}
      {isUpdatePermission && (
        <ConfirmModal
          onClose={handlePasswordConfirmModalClose}
          asyncOperation={generatePasswordAsyncOperation}
          open={passwordConfirmModalOpen}
          closeSubmitModal={_closeSubmitModalWithEditAndDelete}
          closeModalAfterSubmit={closeModalAfterSubmit}
          confirmMessage={passwordConfirmMessage as string}
        ></ConfirmModal>
      )}
    </BootstrapModal>
  );
};

export default SubmitModalWithEditAndDelete;
